import { useAdminContext } from "react-bricks/frontend"
import { useIsomorphicLayoutEffect } from "usehooks-ts"
import { useState } from "react"

type UseMediaQueryOptions = {
    defaultValue?: boolean
    initializeWithValue?: boolean
}

const IS_SERVER = typeof window === "undefined"

export function useMediaQuery(
    query: string,
    { defaultValue = false, initializeWithValue = true }: UseMediaQueryOptions = {}
): boolean {
    const { isAdmin } = useAdminContext()

    const adminFrameDocument = !IS_SERVER
        ? (document.getElementById("rb-admin-frame") as HTMLIFrameElement | null)?.contentWindow?.document
        : undefined
    const adminContentFrameWindow = (adminFrameDocument?.getElementById("rb-content-frame") as HTMLIFrameElement | null)
        ?.contentWindow?.window

    const getMatches = (query: string): boolean => {
        if (IS_SERVER) {
            return defaultValue
        }

        return (isAdmin && adminContentFrameWindow ? adminContentFrameWindow : window).matchMedia(query).matches
    }

    const [matches, setMatches] = useState<boolean>(() => {
        if (initializeWithValue) {
            return getMatches(query)
        }
        return defaultValue
    })

    // Handles the change event of the media query.
    function handleChange() {
        setMatches(getMatches(query))
    }

    useIsomorphicLayoutEffect(() => {
        const matchMedia = (isAdmin && adminContentFrameWindow ? adminContentFrameWindow : window).matchMedia(query)

        // Triggered at the first client-side load and if query changes
        handleChange()

        // Use deprecated `addListener` and `removeListener` to support Safari < 14 (#135)
        if (matchMedia.addListener) {
            matchMedia.addListener(handleChange)
        } else {
            matchMedia.addEventListener("change", handleChange)
        }

        return () => {
            if (matchMedia.removeListener) {
                matchMedia.removeListener(handleChange)
            } else {
                matchMedia.removeEventListener("change", handleChange)
            }
        }
    }, [query, isAdmin])

    return matches
}
